import { SonarQubeApi } from './sonarqube-api';

const delay = (ms = 300) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

// Used by `yarn start` (sonarqube.useMockApi) — never in docker-compose/production builds.
export const mockSonarQubeApi: SonarQubeApi = {
  async createServiceGroup(request) {
    await delay();
    if (request.applicationKey.endsWith('_exists')) {
      throw new Error('Tooling group with key already exists');
    }
    return { id: crypto.randomUUID() };
  },
  async getServiceGroup(serviceGroupId) {
    await delay();
    return {
      id: serviceGroupId,
      productTeamId: '00000000-0000-0000-0000-000000000001',
      applicationKey: 'mock-team_product',
      applicationName: 'Mock Team Product',
      applicationDescription: '',
      createdAt: new Date().toISOString(),
      createdBy: 'user:default/guest',
      updatedAt: null,
      updatedBy: null,
      applicationUrl: 'https://sonarqube.example.com/dashboard?id=mock-team_product',
    };
  },
  async updateServiceGroup(_serviceGroupId, request) {
    await delay();
    if (request.applicationName?.endsWith('Forbidden')) {
      throw new Error('Forbidden');
    }
  },
  async getMembership() {
    await delay();
    return { role: 'admin' };
  },
  async createProject(_serviceGroupId, request) {
    await delay();
    if (request.repositoryName.endsWith('-forbidden')) {
      throw new Error('You need at least maintain access to the repository');
    }
  },
  async updateProject(_serviceGroupId, _projectKey, request) {
    await delay();
    if (request.projectDescription?.endsWith('forbidden')) {
      throw new Error('You need at least maintain access to the repository');
    }
  },
  async getProjects() {
    await delay();
    return [
      {
        key: 'mock-team_product_api',
        name: 'API',
        projectUrl: 'https://sonarqube.example.com/dashboard?id=mock-team_product_api',
      },
      {
        key: 'mock-team_product_web',
        name: 'Web',
        projectUrl: 'https://sonarqube.example.com/dashboard?id=mock-team_product_web',
      },
      {
        key: 'mock-team_product_worker',
        name: 'Worker',
        projectUrl: 'https://sonarqube.example.com/dashboard?id=mock-team_product_worker',
      },
      {
        key: 'mock-team_product_docs',
        name: 'Docs',
        projectUrl: 'https://sonarqube.example.com/dashboard?id=mock-team_product_docs',
      },
      {
        key: 'mock-team_product_new',
        name: 'New',
        projectUrl: 'https://sonarqube.example.com/dashboard?id=mock-team_product_new',
      },
    ];
  },
  async getProjectStatuses() {
    await delay(900);
    return [
      {
        projectKey: 'mock-team_product_api',
        status: 'OK',
        message: null,
      },
      {
        projectKey: 'mock-team_product_web',
        status: 'ERROR',
        message: 'new_coverage, new_duplicated_lines_density',
      },
      {
        projectKey: 'mock-team_product_worker',
        status: 'WARN',
        message: null,
      },
      {
        projectKey: 'mock-team_product_docs',
        status: 'NONE',
        message: null,
      },
      {
        projectKey: 'mock-team_product_new',
        status: null,
        message: null,
      },
    ];
  },
  async getProjectBindings() {
    await delay(600);
    return [
      {
        projectKey: 'mock-team_product_api',
        alm: 'github',
        repository: 'arla/mock-api',
        repositoryUrl: 'https://github.com/arla/mock-api',
      },
      {
        projectKey: 'mock-team_product_web',
        alm: null,
        repository: null,
        repositoryUrl: null,
      },
    ];
  },
  async getProjectDescriptions() {
    await delay(500);
    return [
      {
        projectKey: 'mock-team_product_api',
        description:
          'Public REST API serving product data to the web shop and partner integrations. Handles authentication, rate limiting and caching of catalogue lookups.',
      },
      {
        projectKey: 'mock-team_product_web',
        description: 'Customer-facing web shop.',
      },
      {
        projectKey: 'mock-team_product_new',
        description: null,
      },
    ];
  },
  async getProjectQualityGates() {
    await delay(1200);
    return [
      { projectKey: 'mock-team_product_api', name: 'Arla default', isDefault: true },
      { projectKey: 'mock-team_product_web', name: 'Strict', isDefault: false },
    ];
  },
  async getGroups() {
    await delay();
    return [
      {
        id: 'mock-admin-group-id',
        name: 'mock-team_product_admin',
        users: [
          {
            id: 'mock-sonarqube-user-1',
            entraId: '00000000-0000-0000-0000-000000000001',
            email: 'admin@example.com',
            isAuthorized: true,
          },
        ],
      },
      {
        id: 'mock-developer-group-id',
        name: 'mock-team_product_developer',
        users: [
          {
            id: 'mock-sonarqube-user-2',
            entraId: null,
            email: 'added-in-sonarqube@example.com',
            isAuthorized: false,
          },
        ],
      },
    ];
  },
  addUserToGroup: () => delay(),
  removeUserFromServiceGroup: () => delay(),
  deleteServiceGroup: () => delay(),
  async listQualityGates() {
    await delay();
    return [
      { name: 'Sonar way', isDefault: false, isBuiltIn: true },
      { name: 'Arla default', isDefault: true, isBuiltIn: false },
      { name: 'Strict', isDefault: false, isBuiltIn: false },
    ];
  },
};
